import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle2,
  Clock,
  Loader2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Mail,
  Lock,
} from "lucide-react";
import { getInvoiceByReference } from "@/lib/api/credits.functions";
import { getMyCreditState } from "@/lib/api/credits.functions";
import { resendInvoiceEmail } from "@/lib/api/invoice-resend.functions";
import { reconcileOrderByReference } from "@/lib/api/payment-reconcile.functions";
import { supabase } from "@/integrations/supabase/client";
import { Coins, RefreshCw, Sparkles } from "lucide-react";
import { RetryablePayfastCheckout } from "@/components/site/RetryablePayfastCheckout";


export const Route = createFileRoute("/payment-status/$ref")({
  head: ({ params }) => ({
    meta: [
      {
        title: `Payment status ${params.ref} — Nkadimeng Danny Legal`,
      },
      {
        name: "description",
        content:
          "Live PayFast payment status — see whether your order is pending, processing, paid, failed or cancelled.",
      },
      { name: "robots", content: "noindex,follow" },
    ],
  }),
  component: PaymentStatusPage,
  errorComponent: ({ error }) => (
    <div className="container-narrow py-24 text-center text-sm text-muted-foreground">
      {error.message}
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-narrow py-24 text-center text-sm text-muted-foreground">
      No payment found for that reference.
    </div>
  ),
});

type Step = {
  key: "created" | "checkout" | "processing" | "settled";
  label: string;
  hint: string;
};

const STEPS: Step[] = [
  { key: "created", label: "Order created", hint: "Invoice generated" },
  { key: "checkout", label: "Sent to PayFast", hint: "Card / EFT / SnapScan" },
  { key: "processing", label: "Webhook processing", hint: "Confirming payment" },
  { key: "settled", label: "Outcome", hint: "Paid · failed · cancelled" },
];

function PaymentStatusPage() {
  const { ref } = useParams({ from: "/payment-status/$ref" });
  const fetchInvoice = useServerFn(getInvoiceByReference);
  const resend = useServerFn(resendInvoiceEmail);
  const reconcile = useServerFn(reconcileOrderByReference);
  const qc = useQueryClient();
  const [resending, setResending] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [viewerEmail, setViewerEmail] = useState<string>("");
  useEffect(() => {
    supabase.auth.getUser().then((r) => setViewerEmail(r.data.user?.email ?? ""));
  }, []);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["invoice", ref],
    queryFn: () => fetchInvoice({ data: { reference: ref } }),
    retry: false,
    // Auto-poll every 4s until PayFast posts a terminal outcome.
    // Realtime below usually fires first; polling is the safety net for
    // mobile browsers that drop the websocket while backgrounded.
    refetchInterval: (q) => {
      const status = (q.state.data as any)?.order?.status;
      if (status === "paid" || status === "cancelled" || status === "failed") {
        return false;
      }
      return 4000;
    },
    refetchIntervalInBackground: true,
  });

  async function handleResendInvoice() {
    setResending(true);
    try {
      const r = await resend({ data: { reference: ref } });
      if ((r as any).ok === false) {
        toast.error("Could not resend invoice", {
          description: (r as any).message ?? "Try again shortly.",
        });
      } else {
        toast.success("Proforma invoice re-sent", {
          description: "Check your inbox in a minute or two.",
        });
      }
    } catch (e: any) {
      toast.error("Resend failed", { description: e?.message ?? String(e) });
    } finally {
      setResending(false);
    }
  }

  async function handleReconcile() {
    setReconciling(true);
    try {
      const r: any = await reconcile({ data: { reference: ref } });
      await qc.invalidateQueries({ queryKey: ["invoice", ref] });
      if (!r?.ok) {
        toast.error("Could not verify with PayFast", {
          description: r?.error ?? "No matching order found for this reference.",
        });
      } else if (r.action === "flipped_to_paid") {
        toast.success("Payment confirmed", {
          description: "PayFast webhook was already received — marked as paid.",
        });
      } else if (r.action === "no_change") {
        toast.info("Status already up to date", {
          description: `Current status: ${r.status ?? "unknown"}.`,
        });
      } else {
        toast.info("No PayFast confirmation yet", {
          description: "Give it a few more seconds. This page auto-refreshes when the webhook lands.",
        });
      }
    } catch (e: any) {
      toast.error("Verify failed", { description: e?.message ?? String(e) });
    } finally {
      setReconciling(false);
    }
  }



  // Realtime — same channels the invoice page uses.
  useEffect(() => {
    if (!ref) return;
    const channel = supabase
      .channel(`payment-status-${ref}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "credit_orders",
          filter: `reference=eq.${ref}`,
        },
        () => qc.invalidateQueries({ queryKey: ["invoice", ref] }),
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "contract_orders",
          filter: `reference=eq.${ref}`,
        },
        () => qc.invalidateQueries({ queryKey: ["invoice", ref] }),
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "cv_orders",
          filter: `reference=eq.${ref}`,
        },
        () => qc.invalidateQueries({ queryKey: ["invoice", ref] }),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [ref, qc]);

  if (isLoading) {
    return (
      <div className="container-narrow py-16 text-center text-sm text-muted-foreground">
        <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin" />
        Loading payment status…
      </div>
    );
  }
  if (error) {
    return (
      <div className="container-narrow py-16 text-center text-sm text-muted-foreground">
        {(error as Error).message}
      </div>
    );
  }
  if (!data) return null;

  const o = data.order as any;
  const status: "pending" | "paid" | "cancelled" | "failed" =
    o.status === "paid"
      ? "paid"
      : o.status === "cancelled"
        ? "cancelled"
        : o.status === "failed"
          ? "failed"
          : "pending";

  // Visual progression: created → checkout → processing → settled
  // Without explicit "processing" status from PayFast, we treat any pending
  // order whose paidProviderRef is set as "processing" (webhook in flight).
  const hasProviderRef = Boolean(o.paidProviderRef);
  const currentStepIdx =
    status === "paid" || status === "failed" || status === "cancelled"
      ? 3
      : hasProviderRef
        ? 2
        : 1;

  return (
    <div className="bg-background">
      <section className="bg-gradient-to-b from-navy/5 to-transparent py-10">
        <div className="container-narrow">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
          <p className="mt-3 text-xs uppercase tracking-[0.3em] text-gold">
            Payment tracker
          </p>
          <h1 className="font-display text-3xl text-navy">{o.reference}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {o.title} · R{Number(o.amountZar).toFixed(2)}
          </p>
        </div>
      </section>

      <section className="py-8">
        <div className="container-narrow">
          <StatusBanner
            status={status}
            hasProviderRef={hasProviderRef}
            createdAt={o.createdAt}
            reference={o.reference}
          />

          {data.kind === "credit" ? (
            <LiveCreditsCard reference={o.reference} status={status} />
          ) : null}




          <div className="mt-8 rounded-2xl border border-border bg-card p-6">
            <h2 className="font-display text-lg text-navy">Progress</h2>
            <ol className="mt-5 space-y-4">
              {STEPS.map((step, i) => {
                const state =
                  i < currentStepIdx
                    ? "done"
                    : i === currentStepIdx
                      ? "active"
                      : "todo";
                return (
                  <li key={step.key} className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                        state === "done"
                          ? "bg-emerald-600 text-white"
                          : state === "active"
                            ? "bg-amber-400 text-navy animate-pulse"
                            : "bg-muted text-muted-foreground"
                      }`}
                    >
                      {state === "done" ? "✓" : i + 1}
                    </span>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-navy">
                        {step.label}
                        {i === 3 && status === "paid" ? " — Paid" : ""}
                        {i === 3 && status === "failed" ? " — Failed" : ""}
                        {i === 3 && status === "cancelled"
                          ? " — Cancelled"
                          : ""}
                      </p>
                      <p className="text-[12px] text-muted-foreground">
                        {step.hint}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>

            <dl className="mt-6 space-y-2 border-t border-border pt-5 text-sm">
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Status</dt>
                <dd>
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${
                      status === "paid"
                        ? "bg-emerald-100 text-emerald-800"
                        : status === "failed"
                          ? "bg-rose-100 text-rose-800"
                          : status === "cancelled"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-sky-100 text-sky-800"
                    }`}
                  >
                    {status}
                  </span>
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Amount</dt>
                <dd className="font-semibold text-navy">
                  R{Number(o.amountZar).toFixed(2)}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Order reference</dt>
                <dd className="font-mono">{o.reference}</dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">
                  PayFast transaction ref
                </dt>
                <dd className="font-mono">
                  {o.paidProviderRef ?? (
                    <span className="text-muted-foreground italic">
                      pending…
                    </span>
                  )}
                </dd>
              </div>
              <div className="flex items-center justify-between gap-3">
                <dt className="text-muted-foreground">Created</dt>
                <dd>{new Date(o.createdAt).toLocaleString("en-ZA")}</dd>
              </div>
              {o.paidAt && (
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-muted-foreground">Paid at</dt>
                  <dd>{new Date(o.paidAt).toLocaleString("en-ZA")}</dd>
                </div>
              )}
            </dl>


            <div className="mt-6 flex flex-wrap gap-3">
              {status === "paid" && data.kind === "contract" && o.downloadToken && (
                <a
                  href={`/api/public/contract?ref=${encodeURIComponent(o.reference)}&token=${encodeURIComponent(o.downloadToken)}`}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Download your contract <ArrowRight className="h-3.5 w-3.5" />
                </a>
              )}
              {status !== "paid" && data.kind === "contract" && (
                <span className="inline-flex items-center gap-1 rounded-md border border-dashed border-amber-400 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900">
                  <Lock className="h-3.5 w-3.5" />
                  Contract unlocks after PayFast confirms payment
                </span>
              )}
              {status === "paid" && data.kind === "credit" && (
                <Link
                  to={"/portal/credits" as any}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  View your credits <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
              {status === "paid" && data.kind === "cv" && (
                <Link
                  to={"/portal/cv" as any}
                  className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Go to CV Builder <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              )}
              {status === "paid" && (data.kind === "credit" || data.kind === "cv" || data.kind === "contract") && (o as any).publicToken && (
                <a
                  href={`/api/public/receipt?ref=${encodeURIComponent(o.reference)}&t=${encodeURIComponent((o as any).publicToken)}&doc=receipt`}
                  className="inline-flex items-center gap-1 rounded-md border border-emerald-500 bg-white px-4 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-50"
                  aria-label={`Download PDF receipt for order ${o.reference}`}
                >
                  Download PDF receipt
                </a>
              )}


              <Link
                to="/invoice/$ref"
                params={{ ref: o.reference }}
                className="inline-flex items-center gap-1 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy/90"
              >
                Open invoice <ArrowRight className="h-3.5 w-3.5" />
              </Link>
              {status !== "paid" && (
                <>
                  <button
                    type="button"
                    onClick={handleReconcile}
                    disabled={reconciling}
                    className="inline-flex items-center gap-1 rounded-md border border-sky-500 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-900 hover:bg-sky-100 disabled:opacity-60"
                    aria-label="Verify payment status with PayFast now"
                  >
                    {reconciling ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3.5 w-3.5" />
                    )}
                    Verify with PayFast now
                  </button>
                  <Link
                    to="/invoice/$ref"
                    params={{ ref: o.reference }}
                    search={{ payfast: "cancel" as const }}
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold text-navy hover:border-navy"
                  >
                    Retry payment
                  </Link>
                  <button
                    type="button"
                    onClick={handleResendInvoice}
                    disabled={resending}
                    className="inline-flex items-center gap-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-semibold text-navy hover:border-navy disabled:opacity-60"
                  >
                    {resending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Mail className="h-3.5 w-3.5" />
                    )}
                    Resend proforma invoice
                  </button>
                </>
              )}
            </div>


            <p className="mt-4 text-[11px] text-muted-foreground">
              This page polls PayFast every few seconds and updates the moment
              the webhook finishes — no need to reload.
              {isFetching ? " Checking now…" : ""}
            </p>

          </div>
        </div>
      </section>
    </div>
  );
}

const SUPPORT_EMAIL = "info@nkadimengdannylcs.com";
const SUPPORT_WHATSAPP = "0712766198";
const TIMEOUT_MINUTES = 15;

function NextSteps({
  reference,
  tone,
}: {
  reference: string;
  tone: "rose" | "amber";
}) {
  const toneClass =
    tone === "rose"
      ? "border-rose-200 bg-white text-rose-900"
      : "border-amber-200 bg-white text-amber-900";
  return (
    <div className={`mt-3 rounded-lg border p-3 text-[12px] ${toneClass}`}>
      <p className="font-semibold">What to do next</p>
      <ol className="mt-1 list-decimal space-y-0.5 pl-4">
        <li>Check your bank app — no debit means no funds were taken.</li>
        <li>
          Tap <strong>Retry payment</strong> below to relaunch the PayFast
          gateway with the same invoice reference.
        </li>
        <li>
          Still stuck? Email{" "}
          <a
            className="font-semibold underline"
            href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
              `Payment help — ${reference}`,
            )}`}
          >
            {SUPPORT_EMAIL}
          </a>{" "}
          or WhatsApp{" "}
          <a
            className="font-semibold underline"
            href={`https://wa.me/27${SUPPORT_WHATSAPP.replace(/^0/, "")}?text=${encodeURIComponent(
              `Hi, I need help with payment ${reference}.`,
            )}`}
            target="_blank"
            rel="noreferrer"
          >
            {SUPPORT_WHATSAPP}
          </a>
          . Quote reference <span className="font-mono">{reference}</span>.
        </li>
      </ol>
    </div>
  );
}

function StatusBanner({
  status,
  hasProviderRef,
  createdAt,
  reference,
}: {
  status: "pending" | "paid" | "cancelled" | "failed";
  hasProviderRef: boolean;
  createdAt: string;
  reference: string;
}) {
  if (status === "paid") {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-900">
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
        <div>
          <p className="font-semibold">Paid via PayFast.</p>
          <p className="mt-0.5 text-[12px]">
            Your branded receipt is available on the invoice page.
          </p>
        </div>
      </div>
    );
  }
  if (status === "failed") {
    return (
      <div className="rounded-xl border border-rose-300 bg-rose-50 p-4 text-sm text-rose-900">
        <div className="flex items-start gap-3">
          <XCircle className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">
              Payment failed at PayFast.
            </p>
            <p className="mt-0.5 text-[12px]">
              The bank declined the transaction or it was abandoned. No funds
              were taken from your account.
            </p>
          </div>
        </div>
        <NextSteps reference={reference} tone="rose" />
        <RetryablePayfastCheckout reference={reference} />
      </div>
    );
  }
  if (status === "cancelled") {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Payment cancelled.</p>
            <p className="mt-0.5 text-[12px]">
              Invoice stays open — retry whenever you're ready.
            </p>
          </div>
        </div>
        <NextSteps reference={reference} tone="amber" />
        <RetryablePayfastCheckout reference={reference} />
      </div>
    );
  }
  // Pending — detect timeout (no terminal outcome after TIMEOUT_MINUTES).
  const ageMin = createdAt
    ? Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000)
    : 0;
  const isTimedOut = ageMin >= TIMEOUT_MINUTES;
  if (isTimedOut) {
    return (
      <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">
              PayFast hasn't confirmed after {ageMin} minutes.
            </p>
            <p className="mt-0.5 text-[12px]">
              The gateway usually finalises within a minute or two. If your
              bank already debited the amount, do <strong>not</strong> retry —
              contact us with the reference and we'll reconcile manually.
            </p>
          </div>
        </div>
        <NextSteps reference={reference} tone="amber" />
        <RetryablePayfastCheckout reference={reference} />
      </div>
    );
  }
  return (
    <div className="flex items-start gap-3 rounded-xl border border-sky-300 bg-sky-50 p-4 text-sm text-sky-900">
      <Clock className="mt-0.5 h-5 w-5 shrink-0 animate-pulse" />
      <div>
        <p className="font-semibold">
          {hasProviderRef
            ? "PayFast is finalising your payment…"
            : "Awaiting payment…"}
        </p>
        <p className="mt-0.5 text-[12px]">
          This page refreshes automatically — no need to reload.
        </p>
      </div>
    </div>
  );
}


/**
 * Shown on the credit top-up status page. Pulls the signed-in user's live
 * credit balance + most recent ledger entry, then subscribes to
 * `user_credits` and `credit_ledger` realtime channels so the balance flips
 * up the moment the PayFast ITN webhook grants credits — no reload, no poll.
 */
function LiveCreditsCard({
  reference,
  status,
}: {
  reference: string;
  status: "pending" | "paid" | "cancelled" | "failed";
}) {
  const fetchState = useServerFn(getMyCreditState);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["my-credits", "payment-status", reference],
    queryFn: () => fetchState(),
    retry: false,
    refetchInterval: status === "paid" ? false : 5000,
  });

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth?.user?.id;
      if (!uid || cancelled) return;
      channel = supabase
        .channel(`live-credits-${uid}-${reference}`)
        .on(
          "postgres_changes",
          {
            event: "*",
            schema: "public",
            table: "user_credits",
            filter: `user_id=eq.${uid}`,
          },
          () =>
            qc.invalidateQueries({
              queryKey: ["my-credits", "payment-status", reference],
            }),
        )
        .on(
          "postgres_changes",
          {
            event: "INSERT",
            schema: "public",
            table: "credit_ledger",
            filter: `user_id=eq.${uid}`,
          },
          () =>
            qc.invalidateQueries({
              queryKey: ["my-credits", "payment-status", reference],
            }),
        )
        .subscribe();
    })();
    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [reference, qc]);

  if (isLoading || !data) {
    return (
      <div className="mt-4 rounded-xl border border-border bg-card p-4 text-sm text-muted-foreground">
        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
        Checking your live credit balance…
      </div>
    );
  }
  const balance = (data as any).balance ?? 0;
  const lastGrant = ((data as any).ledger as Array<any> | undefined)?.find(
    (l) => l.ref === reference && Number(l.delta) > 0,
  );

  return (
    <div
      className={`mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border p-4 text-sm ${
        status === "paid"
          ? "border-emerald-300 bg-emerald-50 text-emerald-900"
          : "border-border bg-card text-foreground"
      }`}
    >
      <div className="flex items-center gap-3">
        <Coins className="h-6 w-6 text-gold" />
        <div>
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">
            Live AI credit balance
          </p>
          <p className="font-display text-2xl text-navy">{balance}</p>
        </div>
      </div>
      {status === "paid" && lastGrant ? (
        <div className="flex items-center gap-2 text-emerald-900">
          <Sparkles className="h-4 w-4" />
          <span className="text-sm font-semibold">
            +{Number(lastGrant.delta)} credits added for this order
          </span>
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">
          Updates the second PayFast confirms — no reload needed.
        </p>
      )}
    </div>
  );
}
